import React, { useEffect, useState } from "react";
import accountsApi from "../../services/accountsCommonService";
import { formatINR } from "./accountUser";
import { getErrorMessage, getFieldErrors } from "./accountErrors";

const STATUS = {
  POSTED: ["Posted", "ok"],
  CANCELLED: ["Cancelled", "bad"],
  CREDIT_HOLD: ["Credit hold", "warn"],
  APPROVED: ["Approved", "ok"],
  LOADING: ["Loading", "info"],
  INVOICED: ["Invoiced", "muted"],
  OPEN: ["Open", "info"],
  PARTIAL: ["Part paid", "info"],
  PAID: ["Paid", "ok"],
  Unpaid: ["Unpaid", "warn"],
};

const INVOICE_STATUS = {
  OPEN: ["Unpaid", "warn"],
  PARTIAL: ["Part paid", "info"],
  PAID: ["Paid", "ok"],
  CANCELLED: ["Cancelled", "bad"],
};

export function StatusChip({ status, label, kind }) {
  const pair = (kind === "invoice" ? INVOICE_STATUS : STATUS)[status];
  const text = label || (pair ? pair[0] : status || "");
  const tone = pair ? pair[1] : "";
  return <span className={`accounts-chip ${tone}`}>{text}</span>;
}

export function ApiAlert({ error }) {
  const message = getErrorMessage(error);
  const fields = getFieldErrors(error);
  const list = Object.values(fields);
  if (!message && list.length === 0) return null;
  return (
    <div className="accounts-error" role="alert">
      {message && <div>{message}</div>}
      {list.length > 0 && (
        <ul>{list.map((item) => <li key={item}>{item}</li>)}</ul>
      )}
    </div>
  );
}

export function FormDialog({ title, onClose, onSubmit, children, footer, wide = false }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.activeElement;
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && previous.focus) previous.focus();
    };
  }, [onClose]);

  return (
    <div className={`accounts-modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
      <form className="accounts-panel accounts-dialog" onSubmit={onSubmit} noValidate>
        <div className="accounts-dialog-head">
          <h3>{title}</h3>
          <button type="button" className="accounts-dialog-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="accounts-dialog-body">{children}</div>
        {footer && <div className="accounts-dialog-footer">{footer}</div>}
      </form>
    </div>
  );
}

export function FieldHint({ error, help }) {
  return <span className={`accounts-hint ${error ? "tally-error" : ""}`}>{error || help || " "}</span>;
}

export function Notice({ tone, children }) {
  if (!children) return null;
  return <div className={`accounts-note ${tone || "ok"}`}>{children}</div>;
}

export function ReasonDialog({ title, label, confirm, dismiss, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError("");
    try {
      await onConfirm(text.trim());
    } catch (err) {
      setError(err.message || "Request failed");
      setSaving(false);
    }
  };
  return (
    <FormDialog
      title={title}
      onClose={onClose}
      onSubmit={(event) => { event.preventDefault(); submit(); }}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{dismiss}</button>
          <button type="submit" className="btn btn-danger" disabled={!text.trim() || saving}>{saving ? "Saving…" : confirm}</button>
        </>
      )}
    >
      {error && <div className="accounts-error">{error}</div>}
      <label className="tally-field">{label}
        <textarea className="form-control" rows={3} maxLength={500} value={text} placeholder="Why this is being cancelled" onChange={(event) => setText(event.target.value)} />
        <span className="accounts-hint">{text.length}/500</span>
      </label>
    </FormDialog>
  );
}

export function ItemsTable({ items }) {
  const rows = items || [];
  const total = rows.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  return (
    <div className="accounts-table-wrap">
      <table className="accounts-table">
        <thead><tr><th>Product</th><th className="num">Quantity</th><th className="num">Rate</th><th className="num">Amount</th></tr></thead>
        <tbody>
          {rows.map((item, index) => (
            <tr key={`${item.description}-${index}`}>
              <td>{item.description}</td>
              <td className="num">{item.quantity} {item.unit || "BAG"}</td>
              <td className="num">{formatINR(item.rate)}</td>
              <td className="num">{formatINR(item.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4}>No items</td></tr>}
        </tbody>
        {rows.length > 0 && <tfoot><tr><td colSpan={3}>Total</td><td className="num">{formatINR(total)}</td></tr></tfoot>}
      </table>
    </div>
  );
}

export function CreditPanel({ customerId, orderAmount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!customerId) {
      setData(null);
      return undefined;
    }
    let live = true;
    setLoading(true);
    accountsApi.customerCredit(customerId)
      .then((result) => { if (live) setData(result.data); })
      .catch((err) => { if (live) setError(err.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [customerId]);

  if (!customerId) return <p className="accounts-hint">Select a customer to see their credit position.</p>;
  if (!data) return <p className="accounts-hint">{error || "Loading credit position…"}</p>;
  const checking = orderAmount !== undefined && orderAmount !== null;
  const used = Number(data.outstanding) + Number(data.openOrders) + (checking ? Number(orderAmount) || 0 : 0);
  const after = Number(data.creditLimit) - used;
  const percent = data.creditLimit > 0 ? Math.min(100, Math.max(0, (used / data.creditLimit) * 100)) : 100;
  const over = after < 0;
  return (
    <div className={loading ? "accounts-dim" : ""}>
      <CreditRow label="Credit limit" value={formatINR(data.creditLimit)} bold />
      <CreditRow label="Outstanding (owed now)" value={formatINR(data.outstanding)} />
      <CreditRow label="of which already due" value={formatINR(data.amountDue)} negative={data.amountDue > 0} />
      <CreditRow label="Open orders not yet invoiced" value={formatINR(data.openOrders)} />
      {checking && <CreditRow label="This order" value={formatINR(orderAmount)} />}
      <div className="accounts-progress"><span style={{ width: `${percent}%`, background: over ? "#c0392b" : percent > 80 ? "#d69e2e" : "#14524e" }} /></div>
      <CreditRow label={checking ? "Credit left after this order" : "Available credit"} value={formatINR(after)} bold negative={over} />
      <p className="accounts-hint">Credit days: {data.creditDays}</p>
      {checking && Number(orderAmount) > 0 && (
        <div className={`accounts-note ${over ? "warn" : "ok"}`}>
          {over
            ? `Over the credit limit by ${formatINR(-after)}. The order will be saved on credit hold and needs a manager's approval.`
            : "Within the credit limit. The order will be approved on save."}
        </div>
      )}
    </div>
  );
}

function CreditRow({ label, value, bold, negative }) {
  return (
    <div className="accounts-credit-row">
      <span>{label}</span>
      <strong className={negative ? "accounts-money-neg" : ""} style={bold ? undefined : { fontWeight: 500 }}>{value}</strong>
    </div>
  );
}

export function Pager({ page, limit, total, onPage, onLimit, sizes = [10, 20, 50] }) {
  const pages = Math.max(1, Math.ceil((total || 0) / limit) || 1);
  return (
    <div className="accounts-form-actions start">
      <button type="button" className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span className="accounts-hint">Page {page} of {pages}</span>
      <button type="button" className="btn btn-outline-secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      <select className="form-select accounts-pager-size" value={limit} onChange={(event) => onLimit(Number(event.target.value))}>
        {sizes.map((size) => <option key={size} value={size}>{size}</option>)}
      </select>
    </div>
  );
}

export const PRODUCTS = ["OPC 53 Grade Cement", "OPC 43 Grade Cement", "PPC Cement", "PSC Cement"];
